import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

const FAMILIES = [
  { id: 'openai', label: 'OpenAI' },
  { id: 'gemini', label: 'Gemini' },
  { id: 'claude', label: 'Claude' },
  { id: 'deepseek', label: 'DeepSeek' },
  { id: 'kimi', label: 'Kimi' },
  { id: 'glm', label: 'GLM' },
];

const SECTIONS = [
  { id: 'account_setup', label: 'Account creation & Simple Setup interview' },
  { id: 'setup_consultant', label: 'Setup Consultant (tailor the app)' },
  { id: 'novo_voice', label: 'Novo Voice / Desk' },
  { id: 'ai_assistant', label: 'AI Assistant (page editor chat + non-premium editor ops)' },
  { id: 'ai_design', label: 'AI design / site-wide (premium)' },
  { id: 'website_concierge', label: 'Website concierge' },
  { id: 'catalog_content', label: 'Catalog / SEO / marketing text' },
  { id: 'knowledge', label: 'Knowledge embeddings' },
];

const LANGUAGE_OPTIONS = [
  { value: 'zh', label: 'Any Chinese (zh)' },
  { value: 'en', label: 'English (en)' },
  { value: 'es', label: 'Spanish (es)' },
  { value: 'fr', label: 'French (fr)' },
  { value: 'de', label: 'German (de)' },
  { value: 'it', label: 'Italian (it)' },
  { value: 'nl', label: 'Dutch (nl)' },
  { value: 'zh-Hans', label: 'Simplified Chinese (zh-Hans)' },
  { value: 'zh-Hant', label: 'Traditional Chinese (zh-Hant)' },
  { value: 'zh-HK', label: 'Hong Kong Chinese (zh-HK)' },
  { value: 'ja', label: 'Japanese (ja)' },
  { value: 'ko', label: 'Korean (ko)' },
  { value: 'fa', label: 'Persian (fa)' },
  { value: 'ar', label: 'Arabic (ar)' },
  { value: 'he', label: 'Hebrew (he)' },
  { value: 'pt', label: 'Portuguese (pt)' },
  { value: 'ru', label: 'Russian (ru)' },
  { value: 'bn', label: 'Bengali (bn)' },
  { value: 'el', label: 'Greek (el)' },
  { value: 'hi', label: 'Hindi (hi)' },
  { value: 'id', label: 'Indonesian (id)' },
  { value: 'ms', label: 'Malay (ms)' },
  { value: 'pl', label: 'Polish (pl)' },
  { value: 'ro', label: 'Romanian (ro)' },
  { value: 'th', label: 'Thai (th)' },
  { value: 'tr', label: 'Turkish (tr)' },
  { value: 'uk', label: 'Ukrainian (uk)' },
  { value: 'vi', label: 'Vietnamese (vi)' },
];

function normalizeEnabled(value) {
  const src = value && typeof value === 'object' ? value : {};
  const list = (raw) => (Array.isArray(raw) ? raw.map((id) => String(id).trim()).filter(Boolean) : []);
  return {
    openai: list(src.openai),
    gemini: list(src.gemini),
    claude: list(src.claude),
    deepseek: list(src.deepseek),
    kimi: list(src.kimi),
    glm: list(src.glm),
  };
}

function familyHasModels(enabled, familyId) {
  const total = FAMILIES.reduce((sum, family) => sum + (enabled[family.id] || []).length, 0);
  if (total === 0) return true;
  return (enabled[familyId] || []).length > 0;
}

function familyUsable(familyId, enabled, configured, typed) {
  const hasKey = Boolean(configured?.[familyId]) || Boolean(String(typed?.[familyId] || '').trim());
  return hasKey && familyHasModels(enabled, familyId);
}

export default function HostedAISectionRouting({
  sectionAssignments,
  languageAssignments,
  enabledModels,
  providerApiKeysConfigured,
  typedKeys,
  onChange,
}) {
  const enabled = useMemo(() => normalizeEnabled(enabledModels), [enabledModels]);
  const assignments = sectionAssignments && typeof sectionAssignments === 'object' ? sectionAssignments : {};
  const rows = Array.isArray(languageAssignments) ? languageAssignments : [];

  const emit = (nextSections, nextLanguages) => {
    const cleanedSections = {};
    Object.entries(nextSections || {}).forEach(([id, value]) => {
      if (value && value.family) cleanedSections[id] = value;
    });
    onChange({
      sectionAssignments: Object.keys(cleanedSections).length > 0 ? cleanedSections : undefined,
      languageAssignments: nextLanguages && nextLanguages.length > 0 ? nextLanguages : undefined,
    });
  };

  const setSectionFamily = (sectionId, family) => {
    const next = { ...assignments };
    if (!family) {
      delete next[sectionId];
    } else {
      const current = next[sectionId] || {};
      const models = enabled[family] || [];
      const modelId = current.modelId && models.includes(current.modelId) ? current.modelId : undefined;
      next[sectionId] = modelId ? { family, modelId } : { family };
    }
    emit(next, rows);
  };

  const setSectionModel = (sectionId, modelId) => {
    const current = assignments[sectionId];
    if (!current?.family) return;
    const next = {
      ...assignments,
      [sectionId]: modelId ? { family: current.family, modelId } : { family: current.family },
    };
    emit(next, rows);
  };

  const updateLanguageRow = (index, patch) => {
    const next = rows.map((row, i) => (i === index ? { ...row, ...patch } : row));
    emit(assignments, next);
  };

  const addLanguageRow = () => {
    emit(assignments, [...rows, { language: 'en', family: 'openai', sections: ['*'] }]);
  };

  const removeLanguageRow = (index) => {
    emit(assignments, rows.filter((_, i) => i !== index));
  };

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
          Section routing
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Each product section can use one hosted family. Default follows the platform hosted model above.
          Speech: OpenAI/Gemini sections use that provider’s voice when the key is saved. Other sections use
          Apple on-device voice.
        </Typography>
        <Stack spacing={1.5}>
          {SECTIONS.map((section) => {
            const current = assignments[section.id] || {};
            const family = current.family || '';
            const models = family ? enabled[family] || [] : [];
            return (
              <Grid container spacing={1.5} key={section.id} alignItems="center">
                <Grid item xs={12} md={5}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {section.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {section.id}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    label="Family"
                    value={family}
                    onChange={(e) => setSectionFamily(section.id, e.target.value)}
                    SelectProps={{ native: true }}
                  >
                    <option value="">Default</option>
                    {FAMILIES.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.label}
                        {familyUsable(item.id, enabled, providerApiKeysConfigured, typedKeys)
                          ? ''
                          : ' (no key or allowlist)'}
                      </option>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    label="Model"
                    value={current.modelId || ''}
                    disabled={!family}
                    onChange={(e) => setSectionModel(section.id, e.target.value)}
                    SelectProps={{ native: true }}
                  >
                    <option value="">Family default</option>
                    {models.map((id) => (
                      <option key={id} value={id}>
                        {id}
                      </option>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            );
          })}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
          Language routing
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Optional overrides when the app language matches. Example: Chinese (zh) → GLM when GLM is enabled
          and has a saved key. English stays on the section assignment. Do not insert a default Chinese row
          unless you mean to save it. Family must have models in the allowlist.
        </Typography>
        <Stack spacing={2}>
          {rows.map((row, index) => {
            const family = row.family || 'openai';
            const models = enabled[family] || [];
            const selectedSections = Array.isArray(row.sections) && row.sections.length > 0 ? row.sections : ['*'];
            const allSections = selectedSections.includes('*');
            const warnNoKey = !familyUsable(family, enabled, providerApiKeysConfigured, typedKeys);
            return (
              <Box key={`${row.language}-${index}`} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      label="Language"
                      value={row.language || 'en'}
                      onChange={(e) => updateLanguageRow(index, { language: e.target.value })}
                      SelectProps={{ native: true }}
                    >
                      {LANGUAGE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      label="Family"
                      value={family}
                      onChange={(e) =>
                        updateLanguageRow(index, { family: e.target.value, modelId: undefined })
                      }
                      SelectProps={{ native: true }}
                    >
                      {FAMILIES.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.label}
                        </option>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      label="Model"
                      value={row.modelId || ''}
                      onChange={(e) => updateLanguageRow(index, { modelId: e.target.value || undefined })}
                      SelectProps={{ native: true }}
                    >
                      <option value="">Family default</option>
                      {models.map((id) => (
                        <option key={id} value={id}>
                          {id}
                        </option>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Button color="inherit" onClick={() => removeLanguageRow(index)}>
                      Remove
                    </Button>
                  </Grid>
                </Grid>
                {warnNoKey && (
                  <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
                    This family has no saved key or allowlist yet. The rule will be ignored at resolve time until
                    both are present.
                  </Alert>
                )}
                <Box sx={{ mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={allSections}
                        onChange={(e) =>
                          updateLanguageRow(index, { sections: e.target.checked ? ['*'] : [] })
                        }
                      />
                    }
                    label="All sections"
                  />
                  {!allSections && (
                    <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                      {SECTIONS.map((section) => {
                        const checked = selectedSections.includes(section.id);
                        return (
                          <Chip
                            key={section.id}
                            size="small"
                            label={section.id}
                            color={checked ? 'primary' : 'default'}
                            variant={checked ? 'filled' : 'outlined'}
                            onClick={() => {
                              const next = checked
                                ? selectedSections.filter((id) => id !== section.id)
                                : [...selectedSections.filter((id) => id !== '*'), section.id];
                              updateLanguageRow(index, { sections: next.length ? next : ['*'] });
                            }}
                          />
                        );
                      })}
                    </Stack>
                  )}
                </Box>
              </Box>
            );
          })}
          <Button variant="outlined" onClick={addLanguageRow}>
            Add language rule
          </Button>
        </Stack>
      </Paper>
    </Stack>
  );
}
